import { UserOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import AuthContainer from 'components/containers/AuthContainer';
import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { toast } from 'sonner';

type FormValues = { email: string };

const EMAIL_REGEX = /^[A-Za-z0-9+_.-]+@(.+)$/;

export default function PasswordResetSendEmailPage() {
  // Hooks
  const { t } = useTranslation();
  const form = useForm<FormValues>({ mode: 'onChange', defaultValues: { email: '' } });

  const { loading, run } = useRequest(params => ApiUsersManager.askToResetPassword(params), {
    manual: true,
    onSuccess: () => {
      toast.success(t('passwordReset.success.sendEmail'));
    },
    onError: () => {
      toast.error(t('passwordReset.errors.sendEmail'));
    },
  });

  const onFinish = (values: FormValues) => {
    run({ email: values.email });
  };

  return (
    <AuthContainer
      title={t('passwordReset.title')}
      description={t('passwordReset.description.sendEmail')}
      mode="passwordReset"
      bottomLinkTo="login"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFinish)} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="email"
            rules={{
              // The regex also rejects an empty value, as the old `required` + validator pair did.
              validate: value => EMAIL_REGEX.test(value) || t('loginRegister.fields.email.errors.notEmail'),
            }}
            render={({ field }) => (
              <FormItem>
                <div className="relative">
                  <UserOutlined className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
                  <FormControl>
                    <Input
                      {...field}
                      autoCapitalize="none"
                      placeholder={t('passwordReset.fields.email.placeholder')}
                      className="pl-9 lowercase placeholder:normal-case"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" size="lg" className="w-full" disabled={loading} loading={loading}>
            {t('passwordReset.actions.sendEmail')}
          </Button>
        </form>
      </Form>
    </AuthContainer>
  );
}
